<template>
  <div class="human-service-overlay" v-if="show" @click.self="close">
    <div class="human-service-modal">
      <div class="service-header">
        <h3>联系人工客服</h3>
        <button class="close-btn" @click="close">×</button>
      </div>
      
      <div class="service-content">
        <!-- 联系信息表单 -->
        <form @submit.prevent="submitHumanService">
          <!-- 姓名 -->
          <div class="form-group">
            <label for="user-name" class="form-label">
              姓名 <span class="required">*</span>
            </label>
            <input
              id="user-name"
              type="text"
              v-model="userInfo.name"
              placeholder="请输入您的姓名"
              class="form-input"
              required
              :disabled="isSubmitting"
            />
          </div>
          
          <!-- 手机号 -->
          <div class="form-group">
            <label for="user-phone" class="form-label">
              手机号 <span class="required">*</span>
            </label>
            <input
              id="user-phone"
              type="tel"
              v-model="userInfo.phone"
              placeholder="请输入11位手机号码"
              class="form-input"
              maxlength="11"
              required
              :disabled="isSubmitting"
              @blur="validatePhone"
            />
            <div v-if="phoneError" class="error-message">{{ phoneError }}</div>
          </div>
          
          <!-- 验证码 -->
          <div class="form-group">
            <label for="verification-code" class="form-label">
              验证码 <span class="required">*</span>
            </label>
            <div class="verification-group">
              <input
                id="verification-code"
                type="text"
                v-model="userInfo.verificationCode"
                placeholder="请输入6位验证码"
                class="form-input verification-input"
                maxlength="6"
                required
                :disabled="isSubmitting"
              />
              <button 
                type="button" 
                class="send-code-btn"
                :class="{ 
                  'disabled': isCountingDown || !canSendCode || isSubmitting,
                  'loading': loadingSMS 
                }"
                :disabled="isCountingDown || !canSendCode || loadingSMS || isSubmitting"
                @click="sendVerificationCode"
              >
                <span v-if="loadingSMS">发送中...</span>
                <span v-else>{{ countdownText }}</span>
              </button>
            </div>
            <div v-if="smsError" class="error-message">{{ smsError }}</div>
          </div>
          
          <!-- 反馈类型 -->
          <div class="form-group">
            <label class="form-label">
              反馈类型 <span class="required">*</span>
            </label>
            <div class="feedback-types">
              <div class="type-option" v-for="type in feedbackTypes" :key="type.value">
                <input
                  type="radio"
                  :id="'type-' + type.value"
                  :value="type.value"
                  v-model="userInfo.feedbackType"
                  class="type-radio"
                  required
                  :disabled="isSubmitting"
                />
                <label :for="'type-' + type.value" class="type-label">
                  <span class="type-icon">{{ type.icon }}</span>
                  <span class="type-text">{{ type.label }}</span>
                </label>
              </div>
            </div>
          </div>
          
          <!-- 详细内容 -->
          <div class="form-group">
            <label for="detailed-content" class="form-label">
              详细内容 <span class="optional">（可选）</span>
            </label>
            <textarea
              id="detailed-content"
              v-model="userInfo.detailedContent"
              placeholder="请详细描述您的问题或需求，以便客服更好地为您服务..."
              class="form-textarea"
              rows="4"
              maxlength="500"
              :disabled="isSubmitting"
            ></textarea>
            <div class="char-count">{{ userInfo.detailedContent.length }}/500</div>
          </div>
          
          <!-- 提交按钮 -->
          <div class="form-actions">
            <button type="button" class="btn btn-secondary" @click="close" :disabled="isSubmitting">
              取消
            </button>
            <button 
              type="submit" 
              class="btn btn-primary"
              :disabled="isSubmitting || !isFormValid || loadingSubmit"
              :class="{ 'loading': loadingSubmit }"
            >
              <span v-if="loadingSubmit">提交中...</span>
              <span v-else-if="isSubmitting">处理中...</span>
              <span v-else>提交</span>
            </button>
          </div>
        </form>
        
        <!-- 客服提示 -->
        <div class="service-tips">
          <div class="tip-icon">💡</div>
          <div class="tip-content">
            <p>提交后，我们的客服人员将在1个工作日内通过您提供的手机号联系您。</p>
            <p>紧急问题请拨打客服热线：400-xxx-xxxx</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { send_sms_code } from '@/api/common'
import { sendHumanServiceRequest } from '@/api/tenant/humanService'

export default {
  name: 'HumanService',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    conversationInfo: {
      type: Object,
      default: () => ({})
    },
    workspaceSlug: {
      type: String,
      default: ''
    },
  },
  
  data() {
    return {
      userInfo: {
        name: '',
        phone: '',
        verificationCode: '',
        feedbackType: '1',
        detailedContent: ''
      },
      
      feedbackTypes: [
        { value: '1', label: '售前咨询', icon: '🛒' },
        { value: '2', label: '产品了解', icon: '📚' },
        { value: '3', label: '售后支持', icon: '🔧' },
        { value: '4', label: '投诉建议', icon: '📝' },
        { value: '5', label: '闲聊', icon: '💬' },
        { value: '6', label: '其他', icon: '❓' }
      ],
      
      // 验证码相关
      isCountingDown: false,
      countdownSeconds: 60,
      countdownTimer: null,
      loadingSMS: false,
      
      // 提交状态
      isSubmitting: false,
      loadingSubmit: false,
      
      // 错误信息
      phoneError: '',
      smsError: '',
      
      // 缓存验证码发送时间
      smsCountdownKey: 'humanServiceSMS'
    };
  },
  
  computed: {
    countdownText() {
      if (this.isCountingDown) {
        return `${this.countdownSeconds}秒后重发`;
      }
      return '发送验证码';
    },
    
    canSendCode() {
      return this.validatePhoneFormat(this.userInfo.phone);
    },
    
    isFormValid() {
      return this.userInfo.name.trim() && 
             this.userInfo.phone.trim() && 
             this.userInfo.verificationCode.trim() && 
             this.userInfo.feedbackType &&
             !this.phoneError &&
             !this.smsError;
    }
  },
  
  watch: {
    show(newVal) {
      if (newVal) {
        this.resetForm();
        this.checkCountdown();
      }
    },
    
    'userInfo.phone'(newVal) {
      if (newVal) {
        this.phoneError = '';
      }
    },
    
    'userInfo.verificationCode'(newVal) {
      if (newVal) {
        this.smsError = '';
      }
    }
  },
  
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  },
  
  methods: {
    // 验证手机号格式
    validatePhoneFormat(phone) {
      const phoneRegex = /^1[3-9]\d{9}$/;
      return phoneRegex.test(phone);
    },
    
    // 手机号失去焦点验证
    validatePhone() {
      if (!this.userInfo.phone) {
        this.phoneError = '请输入手机号';
        return false;
      }
      
      if (!this.validatePhoneFormat(this.userInfo.phone)) {
        this.phoneError = '请输入正确的11位手机号码';
        return false;
      }
      
      this.phoneError = '';
      return true;
    },
    
    // 检查倒计时
    checkCountdown() {
      const savedTime = localStorage.getItem(this.smsCountdownKey);
      if (savedTime) {
        const remainingTime = Math.ceil((parseInt(savedTime) - Date.now()) / 1000);
        if (remainingTime > 0) {
          this.countdownSeconds = remainingTime;
          this.startCountdown();
        } else {
          localStorage.removeItem(this.smsCountdownKey);
        }
      }
    },
    
    // 发送验证码
    async sendVerificationCode() {
      if (!this.canSendCode || this.isCountingDown || this.loadingSMS) {
        return;
      }
      
      this.loadingSMS = true;
      this.smsError = '';
      
      try {
        const response = await send_sms_code(this.userInfo.phone);
        
        if (response.data && response.status === 200 && response.data.code === 200) {
          this.startCountdown();
          this.$message.success('验证码已发送');
        } else {
          const errorMsg = (response.data && response.data.message) || '发送验证码失败';
          this.smsError = errorMsg;
          this.$message.error(errorMsg);
        }
      } catch (error) {
        console.error('发送验证码失败:', error);
        this.smsError = '发送验证码失败，请稍后重试';
        this.$message.error('发送验证码失败');
      } finally {
        this.loadingSMS = false;
      }
    },
    
    // 开始倒计时
    startCountdown() {
      this.isCountingDown = true;
      this.countdownSeconds = 60;
      
      // 保存过期时间到localStorage
      localStorage.setItem(this.smsCountdownKey, Date.now() + 60000);
      
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      
      this.countdownTimer = setInterval(() => {
        if (this.countdownSeconds <= 1) {
          clearInterval(this.countdownTimer);
          this.isCountingDown = false;
          this.countdownSeconds = 60;
          localStorage.removeItem(this.smsCountdownKey);
        } else {
          this.countdownSeconds--;
        }
      }, 1000);
    },
    
    // 提交表单
    async submitHumanService() {
      // 表单验证
      if (!this.validatePhone()) {
        return;
      }
      
      if (!this.userInfo.name.trim()) {
        this.$message.error('请输入姓名');
        return;
      }
      
      if (!this.userInfo.verificationCode.trim()) {
        this.$message.error('请输入验证码');
        return;
      }
      
      if (this.userInfo.verificationCode.length !== 6) {
        this.$message.error('验证码必须为6位');
        return;
      }
      
      if (!this.userInfo.feedbackType) {
        this.$message.error('请选择反馈类型');
        return;
      }
      
      this.isSubmitting = true;
      this.loadingSubmit = true;
      
      try {
        const serviceData = {
          name: this.userInfo.name.trim(),
          phone: this.userInfo.phone.trim(),
          verificationCode: this.userInfo.verificationCode.trim(),
          feedbackType: this.userInfo.feedbackType,
          detailedContent: this.userInfo.detailedContent.trim(),
          // 对话信息
          conversationId: this.conversationInfo.conversationId || '',
          threadSlug: this.conversationInfo.threadSlug || '',
          workspaceSlug: this.workspaceSlug,
          // 时间戳
          timestamp: Date.now()
        };
        
        console.log('提交人工服务请求:', serviceData);
        
        // 调用API提交数据
        const response = await sendHumanServiceRequest(serviceData);
        
        if (response && response.code === 200) {
          this.$message.success('提交成功！客服将尽快联系您');
          
          // 触发成功事件
          this.$emit('submitted', {
            ...serviceData,
            message: '人工客服请求已提交'
          });
          
          // 关闭弹窗
          this.close();
          
        } else {
          const errorMsg = response?.message || '提交失败';
          this.$message.error(errorMsg);
        }
        
      } catch (error) {
        console.error('提交失败:', error);
        const errorMsg = error.response?.data?.message || error.message || '提交失败，请稍后重试';
        this.$message.error(errorMsg);
      } finally {
        this.isSubmitting = false;
        this.loadingSubmit = false;
      }
    },
    
    // 关闭弹窗
    close() {
      if (this.isSubmitting) {
        this.$confirm('正在提交中，确定要取消吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.$emit('close');
        }).catch(() => {
          // 用户取消关闭
        });
      } else {
        this.$emit('close');
      }
    },
    
    // 重置表单
    resetForm() {
      this.userInfo = {
        name: '',
        phone: '',
        verificationCode: '',
        feedbackType: '1',
        detailedContent: ''
      };
      
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      
      this.isCountingDown = false;
      this.countdownSeconds = 60;
      this.isSubmitting = false;
      this.loadingSubmit = false;
      this.loadingSMS = false;
      this.phoneError = '';
      this.smsError = '';
      
      // 不清除localStorage，保持倒计时状态
    }
  }
};
</script>

<style scoped>
/* 原有样式保持不变，只添加新的错误样式 */

.error-message {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 4px;
  margin-left: 2px;
}

.send-code-btn.loading {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn.loading {
  opacity: 0.7;
  cursor: not-allowed;
}

/* 原有所有样式保持不变 */
.human-service-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

.human-service-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
}

.service-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e1e4e8;
}

.service-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #666;
}

.service-content {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.required {
  color: #ff4d4f;
  margin-left: 2px;
}

.optional {
  color: #999;
  font-weight: normal;
  font-size: 12px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  box-sizing: border-box;
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.form-input::placeholder {
  color: #999;
}

.verification-group {
  display: flex;
  gap: 10px;
}

.verification-input {
  flex: 1;
}

.send-code-btn {
  flex-shrink: 0;
  padding: 0 16px;
  background: #2196f3;
  border: 1px solid #2196f3;
  border-radius: 6px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  min-width: 120px;
}

.send-code-btn:hover:not(.disabled):not(:disabled):not(.loading) {
  background: #1976d2;
  border-color: #1976d2;
}

.send-code-btn.disabled,
.send-code-btn:disabled {
  background: #bbdefb;
  border-color: #bbdefb;
  cursor: not-allowed;
}

.feedback-types {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.type-option {
  position: relative;
}

.type-radio {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.type-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: #f9f9f9;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.type-label:hover {
  border-color: #2196f3;
  background: #e3f2fd;
}

.type-radio:checked + .type-label {
  border-color: #2196f3;
  background: #e3f2fd;
  color: #1976d2;
}

.type-radio:disabled + .type-label {
  opacity: 0.6;
  cursor: not-allowed;
}

.type-icon {
  font-size: 20px;
  margin-bottom: 4px;
}

.type-text {
  font-size: 12px;
  line-height: 1.2;
}

.form-textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  min-height: 80px;
  transition: all 0.2s;
}

.form-textarea:focus {
  outline: none;
  border-color: #2196f3;
  box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
}

.form-textarea::placeholder {
  color: #999;
}

.char-count {
  text-align: right;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.btn {
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;
}

.btn-primary {
  background: #2196f3;
  color: white;
}

.btn-primary:hover:not(:disabled):not(.loading) {
  background: #1976d2;
}

.btn-primary:disabled,
.btn-primary.loading {
  background: #bbdefb;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #666;
  border: 1px solid #e0e0e0;
}

.btn-secondary:hover:not(:disabled) {
  background: #f5f5f5;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.service-tips {
  margin-top: 20px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.tip-icon {
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.tip-content {
  flex: 1;
}

.tip-content p {
  margin: 4px 0;
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

.tip-content p:first-child {
  margin-bottom: 8px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .human-service-modal {
    width: 95%;
    margin: 10px;
  }
  
  .service-content {
    padding: 16px;
  }
  
  .feedback-types {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .btn {
    width: 100%;
  }
}

@media (max-width: 400px) {
  .feedback-types {
    grid-template-columns: 1fr;
  }
  
  .verification-group {
    flex-direction: column;
  }
  
  .send-code-btn {
    width: 100%;
    padding: 10px;
  }
}
</style>